import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  type Placement,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import {
  cloneElement,
  createContext,
  forwardRef,
  type HTMLProps,
  isValidElement,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

interface TooltipOptions {
  initialOpen?: boolean;
  placement?: Placement;
  buttonDirection?: 'row' | 'column';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  delay?: {
    open: number;
    close: number;
  };
}

export function useTooltip({
  initialOpen = false,
  placement = 'top',
  buttonDirection = 'row',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  disabled,
  delay,
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;
  const arrowRef = useRef(null);

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [
      offset(8),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
    strategy: 'fixed',
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
    delay: delay ?? {
      open: 150,
      close: 200,
    },
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      buttonDirection,
      ...interactions,
      ...data,
      disabled,
    }),
    [open, setOpen, arrowRef, buttonDirection, interactions, data, disabled],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;

const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = () => {
  const context = useContext(TooltipContext);

  if (context == null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};

export function Tooltip({ children, ...options }: { children: ReactNode } & TooltipOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const tooltip = useTooltip(options);
  return <TooltipContext.Provider value={tooltip}>{children}</TooltipContext.Provider>;
}

export const TooltipTrigger = forwardRef<
  HTMLElement,
  HTMLProps<HTMLElement> & {
    asChild?: boolean;
  }
>(function TooltipTrigger({ children, asChild = false, ...props }, propRef) {
  const context = useTooltipContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && isValidElement(children)) {
    return cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      }),
    );
  }

  return (
    <div
      ref={ref}
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}
    >
      {children}
    </div>
  );
});

export const TooltipContent = forwardRef<HTMLDivElement, HTMLProps<HTMLDivElement>>(function TooltipContent({ style, ...props }, propRef) {
  const context = useTooltipContext();
  // const ref = useMergeRefs([providers_services.refs.setFloating, propRef]);

  if (!context.open || context.disabled) return null;

  return (
    <FloatingPortal>
      <Content ref={context.refs.setFloating} style={context.floatingStyles} {...context.getFloatingProps(props)}>
        <div
          ref={propRef}
          {...context.getFloatingProps(props)}
          style={{ display: 'flex', flexDirection: context.buttonDirection, alignItems: 'center' }}
        ></div>
        <FloatingArrow
          ref={context.arrowRef}
          context={context.context}
          fill={'rgba(27, 29, 31, 0.90))'}
          startOffset={'50%'}
          stroke={'rgba(27, 29, 31, 0.90))'}
          strokeWidth={1}
          width={10}
          height={6}
        />
      </Content>
    </FloatingPortal>
  );
});

const Content = styled.div`
  //width: min-content;
  //height: min-content;
  background-color: rgba(27, 29, 31, 0.9);
  color: white;
  font-size: 90%;
  padding: 0.6rem 0.8rem;
  border-radius: 4px;
  box-sizing: border-box;
  z-index: 10000;
`;
